import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useRazorpayPayment } from '../../hooks/useRazorpayPayment';
import { toast } from 'react-toastify';
import PaymentProcessingOverlay from '../../components/PaymentProcessingOverlay';

// ✅ Simple Spinner Icon (SVG)
const SpinnerIcon = ({ className }) => (
  <svg
    className={`${className} animate-spin`}
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    ></path>
  </svg>
);

const CustomBuild = ({ units = [], year, branch, semester, subject }) => {
  const [selectedUnits, setSelectedUnits] = useState([]);
  const { initiatePayment, loading, verifying } = useRazorpayPayment();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const handleViewDemo = () => {
    window.open('https://drive.google.com/drive/folders/17fDmMzHlV5Aje-5yCBWaqtS3HyaeBJ3Q?usp=drive_link', '_blank', 'noopener,noreferrer');
  };

  const toggleUnit = (unit) => {
    setSelectedUnits((prev) =>
      prev.find(u => u._id === unit._id)
        ? prev.filter(u => u._id !== unit._id)
        : [...prev, unit]
    );
  };

  const selectAll = () => setSelectedUnits([...units]);
  const clearAll = () => setSelectedUnits([]);

  // Calculate total from actual unit prices
  const total = selectedUnits.reduce((sum, unit) => sum + (unit.price || 0), 0);

  const handlePurchase = async () => {
    if (!isLoggedIn) {
      toast.error("Please login to purchase notes");
      navigate('/login');
      return;
    }
    
    if (selectedUnits.length === 0) {
      toast.warning("Please select at least one unit");
      return;
    }

    const unitIds = selectedUnits.map(u => u._id);
    await initiatePayment({
      amount: total,
      unitId: unitIds,
      subjectId: [],
      branchId: []
    });
  };

  return (
    <>
      <PaymentProcessingOverlay visible={verifying} />
      <section className="bg-gradient-to-br from-gray-900/60 to-gray-900/40 border border-gray-800 rounded-2xl p-6">
      <header className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-bold text-white">Custom Package</h3>
          <p className="text-sm text-gray-400 mt-1 max-w-lg">Pick the exact units you want. Prices shown per unit — your total updates below.</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={selectAll}
            disabled={units.length === 0}
            className="text-indigo-400 text-sm font-medium hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Select all
          </button>
          <button onClick={clearAll} className="text-gray-400 text-sm hover:text-gray-200">Clear</button>
        </div>
      </header>

      {units.length === 0 ? (
        <div className="py-8 text-center text-gray-400">
          <p className="text-lg font-semibold">No units found</p>
          <p className="text-sm">Try a different search to find available notes.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {units.map((unit) => {
              const isSelected = selectedUnits.find(u => u._id === unit._id);
              return (
                <article
                  key={unit._id}
                  className={`relative rounded-lg p-4 flex flex-col justify-between transition-all duration-150 ${
                    isSelected ? 'bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg' : 'bg-slate-800/60 text-gray-200 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="text-base font-semibold">{unit.title}</div>
                    <div className="text-base font-medium">₹{unit.price}</div>
                  </div>

                  <div className="flex items-center gap-2 mt-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDemo();
                      }}
                      className="flex-1 px-3 py-2 text-xs font-medium bg-gray-700 hover:bg-gray-600 text-white rounded-md transition-colors"
                    >
                      View Demo
                    </button>
                    <button
                      onClick={() => toggleUnit(unit)}
                      className={`flex-1 px-3 py-2 text-xs font-medium rounded-md transition-colors ${
                        isSelected 
                          ? 'bg-white text-indigo-600 hover:bg-gray-100' 
                          : 'bg-indigo-600 hover:bg-indigo-500 text-white'
                      }`}
                    >
                      {isSelected ? '✓ Selected' : 'Select'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Purchase Section */}
          {selectedUnits.length > 0 && (
            <div className="mt-6 pt-6 border-t border-gray-700">
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-400">
                    {selectedUnits.length} {selectedUnits.length === 1 ? 'unit' : 'units'} selected
                  </p>
                  <p className="text-2xl font-bold text-white mt-1">
                    Total: ₹{total}
                  </p>
                </div>
                <button
                  onClick={handlePurchase}
                  disabled={loading}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 ${
                    loading 
                      ? 'bg-gray-700 text-gray-300 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:opacity-95 shadow-lg shadow-indigo-500/30'
                  }`}
                >
                  {loading ? (
                    <>
                      <SpinnerIcon className="w-5 h-5" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-1.5 7h12L17 13M9 21a1 1 0 110-2 1 1 0 010 2zm8 0a1 1 0 110-2 1 1 0 010 2z" />
                      </svg>
                      Purchase Now
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
    </>
  );
};

export default CustomBuild;
